// heatmapUtils.js
// Modular heatmap functionality extracted from HeatmapSection
import React from 'react';

// CUSTOMIZABLE COLOR PALETTE - Exact Google Sheets gradient colors
const COLOR_PALETTE = {
    // Color 1: Deep coral-red (Minpoint)
    color1: { r: 217, g: 83, b: 79 },
    // Color 2: Orange-coral 
    color2: { r: 244, g: 164, b: 96 },
    // Color 3: Yellow (Midpoint - 50%)
    color3: { r: 255, g: 235, b: 59 },
    // Color 4: Light green
    color4: { r: 147, g: 196, b: 125 },
    // Color 5: Teal green (Maxpoint)
    color5: { r: 87, g: 166, b: 74 }
  };
  
  /**
   * Heatmap scaling types
   */
  export const SCALING_TYPES = {
    COLUMN_WISE: 'column',
    TABLE_WIDE: 'table',
    FIXED_RANGE: 'fixed'
  };
  
  /**
   * Calculate scaling information for heatmap data
   * @param {Array} data - Array of data objects
   * @param {Array} fields - Array of field names to calculate scaling for
   * @param {string} scalingType - Type of scaling (column, table, or fixed)
   * @returns {Object} Scaling information object
   */
  export const calculateScalingInfo = (data, fields, scalingType = SCALING_TYPES.COLUMN_WISE) => {
    if (!data || data.length === 0 || !fields || fields.length === 0) {
      return { type: scalingType, min: 0, max: 100 };
    }
  
    switch (scalingType) {
      case SCALING_TYPES.COLUMN_WISE: {
        const ranges = {};
        fields.forEach((field, index) => {
          const values = data
            .map(item => getFieldValue(item, field))
            .filter(val => val !== null && val !== undefined && !isNaN(val));
          
          if (values.length > 0) {
            ranges[index] = {
              min: Math.min(...values),
              max: Math.max(...values)
            };
          } else {
            ranges[index] = { min: 0, max: 100 };
          }
        });
        return { type: scalingType, ranges };
      }
      
      case SCALING_TYPES.TABLE_WIDE: {
        const allValues = [];
        fields.forEach(field => {
          const values = data
            .map(item => getFieldValue(item, field))
            .filter(val => val !== null && val !== undefined && !isNaN(val));
          allValues.push(...values);
        });
        
        return {
          type: scalingType,
          min: allValues.length > 0 ? Math.min(...allValues) : 0,
          max: allValues.length > 0 ? Math.max(...allValues) : 100
        };
      }
      
      case SCALING_TYPES.FIXED_RANGE:
      default:
        return { type: scalingType, min: 0, max: 100 };
    }
  };
  
  /**
   * Helper function to get field value from object using dot notation
   * @param {Object} obj - Data object
   * @param {string} field - Field path (e.g., 'registrations.regPercent')
   * @returns {number|null} Field value
   */
  const getFieldValue = (obj, field) => {
    const parts = field.split('.');
    let value = obj;
    
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return null;
      }
    }
    
    return typeof value === 'number' ? value : null;
  };
  
  /**
   * Helper function to create gradient CSS from color palette
   * @returns {string} CSS gradient string
   */
  export const getGradientCSS = () => {
    return `linear-gradient(to right, 
      rgb(${COLOR_PALETTE.color1.r},${COLOR_PALETTE.color1.g},${COLOR_PALETTE.color1.b}), 
      rgb(${COLOR_PALETTE.color2.r},${COLOR_PALETTE.color2.g},${COLOR_PALETTE.color2.b}), 
      rgb(${COLOR_PALETTE.color3.r},${COLOR_PALETTE.color3.g},${COLOR_PALETTE.color3.b}), 
      rgb(${COLOR_PALETTE.color4.r},${COLOR_PALETTE.color4.g},${COLOR_PALETTE.color4.b}), 
      rgb(${COLOR_PALETTE.color5.r},${COLOR_PALETTE.color5.g},${COLOR_PALETTE.color5.b})
    )`;
  };
  
  /**
   * Get heatmap color for a value based on scaling information
   * @param {number} value - The value to get color for
   * @param {number} columnIndex - Index of the column (for column-wise scaling)
   * @param {Object} scalingInfo - Scaling information object
   * @param {Object} options - Additional options
   * @returns {Object} Style object with backgroundColor and color
   */
  export const getHeatmapColor = (value, columnIndex = 0, scalingInfo = null, options = {}) => {
    const {
      useWhiteText = true,
      zeroValueColor = COLOR_PALETTE.color1,
      textColor = null
    } = options;
  
    // Handle zero or invalid values
    if (value === 0 || value === null || value === undefined || isNaN(value)) {
      return {
        backgroundColor: `rgb(${zeroValueColor.r}, ${zeroValueColor.g}, ${zeroValueColor.b})`,
        color: textColor || (useWhiteText ? '#ffffff' : '#000000')
      };
    }
  
    // Determine the scaling range to use
    let minValue, maxValue;
    
    if (scalingInfo?.type === SCALING_TYPES.COLUMN_WISE && scalingInfo.ranges?.[columnIndex]) {
      // Column-wise scaling
      minValue = scalingInfo.ranges[columnIndex].min;
      maxValue = scalingInfo.ranges[columnIndex].max;
    } else if (scalingInfo?.type === SCALING_TYPES.TABLE_WIDE) {
      // Table-wide scaling
      minValue = scalingInfo.min;
      maxValue = scalingInfo.max;
    } else {
      // Fixed range scaling
      minValue = 0;
      maxValue = 100;
    }
    
    // Normalize value to 0-1 range
    const normalizedRatio = maxValue > minValue 
      ? (value - minValue) / (maxValue - minValue)
      : 0.5;
    
    // Ensure ratio is between 0 and 1
    const ratio = Math.max(0, Math.min(1, normalizedRatio));
    
    // Apply gradient using the customizable color palette
    let red, green, blue;
    
    if (ratio <= 0.25) {
      const subRatio = ratio / 0.25;
      red = Math.round(COLOR_PALETTE.color1.r + (COLOR_PALETTE.color2.r - COLOR_PALETTE.color1.r) * subRatio);
      green = Math.round(COLOR_PALETTE.color1.g + (COLOR_PALETTE.color2.g - COLOR_PALETTE.color1.g) * subRatio);
      blue = Math.round(COLOR_PALETTE.color1.b + (COLOR_PALETTE.color2.b - COLOR_PALETTE.color1.b) * subRatio);
    } else if (ratio <= 0.5) {
      const subRatio = (ratio - 0.25) / 0.25;
      red = Math.round(COLOR_PALETTE.color2.r + (COLOR_PALETTE.color3.r - COLOR_PALETTE.color2.r) * subRatio);
      green = Math.round(COLOR_PALETTE.color2.g + (COLOR_PALETTE.color3.g - COLOR_PALETTE.color2.g) * subRatio);
      blue = Math.round(COLOR_PALETTE.color2.b + (COLOR_PALETTE.color3.b - COLOR_PALETTE.color2.b) * subRatio);
    } else if (ratio <= 0.75) {
      const subRatio = (ratio - 0.5) / 0.25;
      red = Math.round(COLOR_PALETTE.color3.r + (COLOR_PALETTE.color4.r - COLOR_PALETTE.color3.r) * subRatio);
      green = Math.round(COLOR_PALETTE.color3.g + (COLOR_PALETTE.color4.g - COLOR_PALETTE.color3.g) * subRatio);
      blue = Math.round(COLOR_PALETTE.color3.b + (COLOR_PALETTE.color4.b - COLOR_PALETTE.color3.b) * subRatio);
    } else {
      const subRatio = (ratio - 0.75) / 0.25;
      red = Math.round(COLOR_PALETTE.color4.r + (COLOR_PALETTE.color5.r - COLOR_PALETTE.color4.r) * subRatio);
      green = Math.round(COLOR_PALETTE.color4.g + (COLOR_PALETTE.color5.g - COLOR_PALETTE.color4.g) * subRatio);
      blue = Math.round(COLOR_PALETTE.color4.b + (COLOR_PALETTE.color5.b - COLOR_PALETTE.color4.b) * subRatio);
    }
    
    const backgroundColor = `rgb(${red}, ${green}, ${blue})`;
    
    return {
      backgroundColor,
      color: textColor || (useWhiteText ? '#ffffff' : '#000000')
    };
  };
  
  /**
   * React hook for managing heatmap state
   * @param {Array} data - Array of data objects
   * @param {Array} fields - Array of field names for heatmap
   * @param {string} initialScalingType - Initial scaling type
   * @returns {Object} Heatmap state and controls
   */
  export const useHeatmap = (data = [], fields = [], initialScalingType = SCALING_TYPES.COLUMN_WISE) => {
    const [enabled, setEnabled] = React.useState(false);
    const [scalingType, setScalingType] = React.useState(initialScalingType);
    
    const scalingInfo = React.useMemo(() => {
      return calculateScalingInfo(data, fields, scalingType);
    }, [data, fields, scalingType]);
    
    const getColor = React.useCallback((value, columnIndex = 0, options = {}) => {
      if (!enabled) return { backgroundColor: 'transparent', color: 'inherit' };
      return getHeatmapColor(value, columnIndex, scalingInfo, options);
    }, [enabled, scalingInfo]);
    
    return {
      enabled,
      setEnabled,
      scalingType,
      setScalingType,
      scalingInfo,
      getColor,
      toggle: () => setEnabled(!enabled)
    };
  };
  
  /**
   * Heatmap Legend Component
   */
  export const HeatmapLegend = ({ scalingInfo, fields = [], className = "" }) => {
    if (!scalingInfo) return null;
    
    return (
      <div className={`text-xs text-gray-500 ${className}`}>
        <p className="font-medium mb-2">Color Scale: {scalingInfo.type === SCALING_TYPES.COLUMN_WISE ? 'Column-wise' : scalingInfo.type === SCALING_TYPES.TABLE_WIDE ? 'Table-wide' : 'Fixed Range'}</p>
        
        {scalingInfo.type === SCALING_TYPES.COLUMN_WISE && scalingInfo.ranges ? (
          <div>
            <p className="text-xs text-gray-600 mb-2">Each column uses its own min/max range</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {fields.map((field, idx) => {
                const range = scalingInfo.ranges[idx];
                return range ? (
                  <div key={field} className="flex items-center gap-2">
                    <span className="font-medium">{field}:</span>
                    <div className="w-12 h-3 rounded" style={{background: getGradientCSS()}}></div>
                    <span>{range.min}% - {range.max}%</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        ) : scalingInfo.type === SCALING_TYPES.TABLE_WIDE ? (
          <div>
            <p className="text-xs text-gray-600 mb-2">Entire table uses same range</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-16 h-4 rounded" style={{background: getGradientCSS()}}></div>
                <span className="text-xs">{scalingInfo.min}% → {scalingInfo.max}%</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-16 h-4 rounded" style={{background: getGradientCSS()}}></div>
              <span className="text-xs">0% → 100%</span>
            </div>
            <span className="text-xs">Fixed range scaling</span>
          </div>
        )}
      </div>
    );
  };
  
  // Export color palette for customization
  export { COLOR_PALETTE };